import React, { createContext, useCallback, useState, useContext } from 'react';
import Swal from 'sweetalert2';
import { IconType } from 'react-icons';
import * as icons from 'react-icons/all';

import { useHistory } from 'react-router-dom';
import api from '../services/api';
import mask from '../utils/mask';

interface IAvatar {
  id: string;
  avatar_url: string;
}

interface IWallpaper {
  id: string;
  wallpaper_url: string;
}

interface IAddress {
  id: string;
  zipcode?: string;
  street?: string;
  number?: string;
  neighborhood?: string;
  city: string;
  state: string;
  complement?: string;
}

interface ISocialNetwork {
  id: string;
  type: string;
  icon?: string | IconType;
  link: string;
}

export interface IUserResponse {
  id: string;
  plan_id: string;
  way_id?: string;
  wallpaper_id?: string;
  name: string;
  email: string;
  foreign: boolean;
  document: string;
  birthdate: string;
  occupation: string;
  phone: string;
  biography: string;
  avatar?: IAvatar;
  wallpaper?: IWallpaper;
  address?: IAddress;
  way?: {
    title: string;
  };
  socialNetworks: ISocialNetwork[];
}

export interface IUser {
  id: string;
  plan_id: string;
  way_id?: string;
  wallpaper_id?: string;
  name: string;
  email: string;
  foreign: boolean;
  document: string;
  birthdate: string;
  occupation: string;
  phone: string;
  biography: string;
  is_support?: boolean;
  way?: {
    title: string;
  };
  avatar?: IAvatar;
  wallpaper?: IWallpaper;
  address?: IAddress;
  socialNetworks: ISocialNetwork[];
  oldSocialNetworks: ISocialNetwork[];
}

interface AuthStateResponse {
  token: string;
  user: IUserResponse;
}

interface AuthState {
  token: string;
  user: IUser;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface AuthContextData {
  user: IUser;
  signIn(credentials: SignInCredentials): Promise<void>;
  autoSignIn(hash: string): Promise<void>;
  signOut(): void;
  updateUser(user: IUser): void;
}

export const AuthContext = createContext<AuthContextData>(
  {} as AuthContextData
);

export const AuthProvider: React.FC = ({ children }) => {
  const history = useHistory();
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@Leiaut:token');
    const user = localStorage.getItem('@Leiaut:user');

    if (token && user) {
      const userData: IUser = JSON.parse(user);

      api.defaults.headers.authorization = `Bearer ${token}`;
      api.defaults.headers.wayid = userData.way_id;
      api.defaults.headers.way_id = userData.way_id;

      const socialNetworks: ISocialNetwork[] = userData.oldSocialNetworks.map(
        (socialNetwork) => {
          const Icons = icons as any;
          return {
            id: socialNetwork.id,
            type: socialNetwork.type,
            icon: socialNetwork.icon ? Icons[socialNetwork.icon as string] : '',
            link: socialNetwork.link,
          };
        }
      );

      userData.socialNetworks = socialNetworks;

      return { token, user: userData };
    }

    return {} as AuthState;
  });

  const signIn = useCallback(async ({ email, password }: SignInCredentials) => {
    try {
      const response = await api.post<AuthStateResponse>(`users/sessions`, {
        email: email.toLowerCase(),
        password,
      });

      const { token, user } = response.data;

      const userData: IUser = {
        id: user.id,
        plan_id: user.plan_id,
        way_id: user.way_id,
        wallpaper_id: user.wallpaper_id,
        name: user.name,
        email: user.email,
        foreign: user.foreign,
        document: user.document
          ? mask({ kind: 'cpf', value: user.document })
          : '-',
        birthdate: user.birthdate,
        occupation: user.occupation,
        phone: user.phone
          ? mask({ kind: 'cel-phone', value: user.phone })
          : '-',
        biography: user.biography,
        avatar: user.avatar,
        wallpaper: user.wallpaper,
        address: user.address
          ? {
              ...user.address,
              zipcode: user.address.zipcode
                ? mask({ kind: 'zip-code', value: user.address.zipcode })
                : '-',
            }
          : undefined,
        way: user.way
          ? {
              title: user.way.title.replace('Sou', ''),
            }
          : undefined,
        socialNetworks: user.socialNetworks,
        oldSocialNetworks: user.socialNetworks,
      };

      localStorage.setItem('@Leiaut:token', token);
      localStorage.setItem('@Leiaut:user', JSON.stringify(userData));

      api.defaults.headers.authorization = `Bearer ${token}`;
      api.defaults.headers.way_id = userData.way_id;

      const socialNetworks: ISocialNetwork[] = user.socialNetworks.map(
        (socialNetwork) => {
          const Icons = icons as any;
          return {
            id: socialNetwork.id,
            type: socialNetwork.type,
            icon: socialNetwork.icon ? Icons[socialNetwork.icon as string] : '',
            link: socialNetwork.link,
          };
        }
      );

      userData.socialNetworks = socialNetworks;

      setData({ token, user: userData });
    } catch (error) {
      console.log(error);
      console.log('AQUI');
      Swal.fire('Opss...', 'Código de autenticação inválido', 'error');
    }
  }, []);

  const autoSignIn = useCallback(
    async (hash) => {
      try {
        const response = await api.post<AuthStateResponse>(
          `users/sessions/auto`,
          {
            hash,
          }
        );

        const { token, user } = response.data;

        const userData: IUser = {
          id: user.id,
          plan_id: user.plan_id,
          way_id: user.way_id,
          wallpaper_id: user.wallpaper_id,
          name: user.name,
          email: user.email,
          foreign: user.foreign,
          document: user.document
            ? mask({ kind: 'cpf', value: user.document })
            : '-',
          birthdate: user.birthdate,
          occupation: user.occupation,
          phone: user.phone
            ? mask({ kind: 'cel-phone', value: user.phone })
            : '-',
          biography: user.biography,
          is_support: true,
          avatar: user.avatar,
          wallpaper: user.wallpaper,
          address: user.address
            ? {
                ...user.address,
                zipcode: user.address.zipcode
                  ? mask({ kind: 'zip-code', value: user.address.zipcode })
                  : '-',
              }
            : undefined,
          way: user.way
            ? {
                title: user.way.title.replace('Sou', ''),
              }
            : undefined,
          socialNetworks: user.socialNetworks,
          oldSocialNetworks: user.socialNetworks,
        };

        localStorage.setItem('@Leiaut:token', token);
        localStorage.setItem('@Leiaut:user', JSON.stringify(userData));

        api.defaults.headers.authorization = `Bearer ${token}`;
        api.defaults.headers.way_id = userData.way_id;

        const socialNetworks: ISocialNetwork[] = user.socialNetworks.map(
          (socialNetwork) => {
            const Icons = icons as any;
            return {
              id: socialNetwork.id,
              type: socialNetwork.type,
              icon: socialNetwork.icon
                ? Icons[socialNetwork.icon as string]
                : '',
              link: socialNetwork.link,
            };
          }
        );

        userData.socialNetworks = socialNetworks;

        setData({ token, user: userData });
        history.push(`${process.env.PUBLIC_URL}/produtos`);
      } catch (error) {
        Swal.fire('Opss...', 'Código de autenticação inválido', 'error');
      }
    },
    [history]
  );

  const signOut = useCallback(async () => {
    localStorage.removeItem('@Leiaut:token');
    localStorage.removeItem('@Leiaut:user');

    setData({} as AuthState);
  }, []);

  const updateUser = useCallback(
    (user: IUser) => {
      localStorage.setItem('@Leiaut:user', JSON.stringify(user));

      const socialNetworks: ISocialNetwork[] = user.oldSocialNetworks.map(
        (socialNetwork) => {
          const Icons = icons as any;
          return {
            id: socialNetwork.id,
            type: socialNetwork.type,
            icon: socialNetwork.icon ? Icons[socialNetwork.icon as string] : '',
            link: socialNetwork.link,
          };
        }
      );

      // eslint-disable-next-line no-param-reassign
      user.socialNetworks = socialNetworks;
      api.defaults.headers.way_id = user.way_id;

      setData({
        token: data.token,
        user,
      });
    },
    [setData, data.token]
  );

  return (
    <AuthContext.Provider
      value={{ user: data.user, signIn, autoSignIn, signOut, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
